import { CheckCircle2, ChevronRight, Trophy, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import useGroupBracketRemoval from '../hooks/useGroupBracketRemoval'
import { Badge } from './ui/badge'
import { Button } from './ui/button'

// get_group_brackets returns brackets newest tournament first; keep that order
// and gather each tournament's brackets under one heading.
function groupByTournament(brackets) {
  const byId = new Map()
  for (const b of brackets) {
    if (!byId.has(b.tournament_id)) {
      byId.set(b.tournament_id, { id: b.tournament_id, name: b.tournament_name, brackets: [] })
    }
    byId.get(b.tournament_id).brackets.push(b)
  }
  return [...byId.values()]
}

export default function GroupBracketsTab({ brackets, groupId, isOwner = false, onChanged }) {
  const navigate = useNavigate()

  const { removeBracket, removeTournament } = useGroupBracketRemoval(groupId, onChanged)

  if (brackets.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No brackets yet</p>
        <p className="text-sm mt-1">
          {isOwner
            ? 'Every bracket you enter is added to your group automatically.'
            : 'Brackets show up here when the group owner enters them.'}
        </p>
        <Button
          onClick={() => navigate('/tournaments')}
          className="mt-4"
        >
          Browse Tournaments
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {groupByTournament(brackets).map((tournament) => (
          <section key={tournament.id}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="font-semibold break-words">{tournament.name}</h3>
              {isOwner && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground flex-shrink-0"
                  onClick={() => removeTournament({ tournamentId: tournament.id, name: tournament.name })}
                >
                  Remove tournament
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {[...tournament.brackets]
                .sort((a, b) => (a.division_name || '').localeCompare(b.division_name || ''))
                .map((bracket) => (
                <div
                  key={bracket.bracket_id}
                  className="p-4 rounded-lg border bg-muted/30 hover:bg-muted/60 transition-colors cursor-pointer group"
                  onClick={() => {
                    if (bracket.user_has_entry && bracket.user_entry_id) {
                      navigate(`/bracket/${bracket.bracket_id}/picks?entryId=${bracket.user_entry_id}`)
                    } else {
                      navigate(`/bracket/${bracket.bracket_id}/picks`)
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium mb-2">{bracket.division_name}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {bracket.sport_name}
                        </Badge>
                        {bracket.bracket_status === 'locked' && (
                          <Badge variant="muted" className="text-xs">
                            Locked
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isOwner && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground flex-shrink-0"
                        aria-label={`Remove ${bracket.division_name} from group`}
                        onClick={(e) => {
                          e.stopPropagation()
                          removeBracket({ bracketId: bracket.bracket_id, label: `${bracket.tournament_name} – ${bracket.division_name}` })
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 mt-1" />
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    {bracket.user_has_entry ? (
                      <div className="flex items-center gap-1.5 text-[var(--teal-11)]">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-sm font-medium">Joined</span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">View bracket</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      <div className="mt-4 text-center">
        <Button
          variant="outline"
          onClick={() => navigate('/tournaments')}
        >
          Add More Brackets
        </Button>
      </div>
    </>
  )
}
